import { useState, useEffect, useMemo, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  EuiSideNav,
  EuiFieldSearch,
  EuiSpacer,
  EuiTitle,
  EuiLoadingSpinner,
  EuiButtonEmpty,
  EuiButton,
  EuiFlexGroup,
  EuiFlexItem,
  EuiCallOut,
  EuiText,
  EuiBadge,
  EuiHorizontalRule,
  EuiIcon,
} from '@elastic/eui'
import showdown from 'showdown'
import parse from 'html-react-parser'

import '../styles/documentation.css'

const DOCS_BASE = '/docs'
const MANIFEST_URL = `${DOCS_BASE}/manifest.json`

const converter = new showdown.Converter({
  tables: true,
  ghCompatibleHeaderId: true,
  strikethrough: true,
  tasklists: true,
  openLinksInNewWindow: true,
  emoji: true,
  ghCodeBlocks: true,
})

function htmlToText(html) {
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function highlight(html, term) {
  if (!term) return html
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const re = new RegExp(`(${escaped})(?![^<]*>)`, 'gi')
  return html.replace(re, '<mark class="cv-doc-highlight">$1</mark>')
}

function buildPageToc(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return Array.from(doc.querySelectorAll('h2, h3')).map((h) => ({
    id: h.id,
    text: h.textContent.trim(),
    level: parseInt(h.tagName[1], 10),
  }))
}

export default function Documentation() {
  const location = useLocation()
  const navigate = useNavigate()
  const [manifest, setManifest] = useState(null)
  const [pages, setPages] = useState([]) // [{ slug, title, section, html, text }]
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')

  // Extraction du slug depuis l'URL : /Documentation/<slug>
  const currentSlug = useMemo(() => {
    const m = location.pathname.match(/^\/Documentation\/?([^/]*)/)
    return m && m[1] ? decodeURIComponent(m[1]) : ''
  }, [location.pathname])

  // Chargement manifest + tous les MDs
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const res = await fetch(MANIFEST_URL)
        if (!res.ok) throw new Error(`Manifest HTTP ${res.status}`)
        const mf = await res.json()
        const fetched = await Promise.all(
          mf.pages.map(async (p) => {
            const r = await fetch(`${DOCS_BASE}/${p.file}`)
            if (!r.ok) throw new Error(`${p.file} HTTP ${r.status}`)
            const rawMd = await r.text()
            // Les liens d'assets sont en relatif (./assets/...) dans le MD ;
            // on les ré-ancre sur /docs/ pour qu'ils marchent depuis n'importe
            // quelle route SPA.
            const md = rawMd.replace(/\]\(\.\/assets\//g, `](${DOCS_BASE}/assets/`)
            const html = converter.makeHtml(md)
            return {
              ...p,
              md,
              html,
              text: htmlToText(html).toLowerCase(),
            }
          }),
        )
        if (!cancelled) {
          setManifest(mf)
          setPages(fetched)
        }
      } catch (err) {
        if (!cancelled) setError(err.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  // Redirige sur la première page si pas de slug
  useEffect(() => {
    if (!loading && !error && pages.length && !currentSlug) {
      navigate(`/Documentation/${pages[0].slug}`, { replace: true })
    }
  }, [loading, error, pages, currentSlug, navigate])

  // Page courante
  const currentPage = useMemo(
    () => pages.find((p) => p.slug === currentSlug),
    [pages, currentSlug],
  )

  // Pages filtrées par la recherche (titre OU contenu)
  const visiblePages = useMemo(() => {
    if (!search.trim()) return pages
    const t = search.trim().toLowerCase()
    return pages.filter(
      (p) => p.title.toLowerCase().includes(t) || p.text.includes(t),
    )
  }, [pages, search])

  // Regroupement des pages par section pour la sidebar
  const sideNavItems = useMemo(() => {
    if (!visiblePages.length) return []
    const sections = {}
    visiblePages.forEach((p) => {
      const sec = p.section || 'Autres'
      if (!sections[sec]) sections[sec] = []
      sections[sec].push(p)
    })
    return Object.entries(sections).map(([section, list], idx) => ({
      id: `section-${idx}`,
      name: section,
      items: list.map((p) => ({
        id: p.slug,
        name: p.title,
        href: `/Documentation/${p.slug}`,
        isSelected: p.slug === currentSlug,
        onClick: (e) => {
          e.preventDefault()
          navigate(`/Documentation/${p.slug}`)
          document.querySelector('.cv-doc-main')?.scrollTo({ top: 0, behavior: 'smooth' })
        },
      })),
    }))
  }, [visiblePages, currentSlug, navigate])

  // HTML de la page courante avec highlight si recherche active
  const pageHtml = useMemo(() => {
    if (!currentPage) return ''
    return search ? highlight(currentPage.html, search) : currentPage.html
  }, [currentPage, search])

  // TOC intra-page (H2 / H3)
  const pageToc = useMemo(
    () => (currentPage ? buildPageToc(currentPage.html) : []),
    [currentPage],
  )

  // Navigation prev / next
  const { prev, next } = useMemo(() => {
    if (!currentPage || !pages.length) return { prev: null, next: null }
    const i = pages.findIndex((p) => p.slug === currentPage.slug)
    return {
      prev: i > 0 ? pages[i - 1] : null,
      next: i < pages.length - 1 ? pages[i + 1] : null,
    }
  }, [pages, currentPage])

  const scrollToAnchor = useCallback((id) => {
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  if (loading) {
    return (
      <div className="cv-doc-page-wrapper">
        <EuiFlexGroup
          justifyContent="center"
          alignItems="center"
          style={{ minHeight: '50vh' }}
        >
          <EuiFlexItem grow={false}>
            <EuiLoadingSpinner size="xl" />
          </EuiFlexItem>
        </EuiFlexGroup>
      </div>
    )
  }

  if (error) {
    return (
      <div className="cv-doc-page-wrapper">
        <EuiCallOut
          title="Impossible de charger la documentation"
          color="danger"
          iconType="alert"
        >
          <p>{error}</p>
          <p>
            Vérifie que les fichiers sont présents dans{' '}
            <code>public/docs/</code>.
          </p>
        </EuiCallOut>
      </div>
    )
  }

  return (
    <div className="cv-doc-page-wrapper">
      {/* Sidebar nav docs */}
      <aside className="cv-doc-nav">
        <div className="cv-doc-nav-header">
          <EuiTitle size="s">
            <h2>{manifest?.title || 'Documentation'}</h2>
          </EuiTitle>
          {manifest && (
            <EuiText size="xs" color="subdued">
              <p>
                Version {manifest.version} · mise à jour le{' '}
                {manifest.updatedAt}
              </p>
            </EuiText>
          )}
        </div>

        <EuiSpacer size="s" />

        <EuiFieldSearch
          placeholder="Rechercher…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          isClearable
          fullWidth
          compressed
        />

        <EuiSpacer size="xs" />

        <EuiButtonEmpty
          iconType="arrowLeft"
          onClick={() => navigate('/Dashboard')}
          size="xs"
          flush="left"
        >
          Retour au Dashboard
        </EuiButtonEmpty>

        <EuiSpacer size="s" />

        {visiblePages.length === 0 ? (
          <EuiCallOut size="s" color="warning" title="Aucun résultat" />
        ) : (
          <EuiSideNav
            aria-label="Sommaire de la documentation"
            mobileTitle="Sommaire"
            items={sideNavItems}
          />
        )}
      </aside>

      {/* Contenu principal */}
      <main className="cv-doc-main">
        {currentPage ? (
          <>
            <div className="cv-doc-breadcrumb">
              {currentPage.section && (
                <EuiBadge color="hollow">{currentPage.section}</EuiBadge>
              )}
              <EuiButtonEmpty
                iconType="download"
                href={`${DOCS_BASE}/${currentPage.file}`}
                download={currentPage.file}
                size="xs"
                flush="right"
              >
                Télécharger cette page (.md)
              </EuiButtonEmpty>
            </div>

            <article className="cv-doc">{parse(pageHtml)}</article>

            <EuiHorizontalRule />

            {/* Navigation prev / next */}
            <EuiFlexGroup
              gutterSize="m"
              alignItems="center"
              justifyContent="spaceBetween"
              className="cv-doc-pager"
            >
              <EuiFlexItem grow={false}>
                {prev && (
                  <EuiButton
                    iconType="arrowLeft"
                    iconSide="left"
                    onClick={() => {
                      navigate(`/Documentation/${prev.slug}`)
                      document.querySelector('.cv-doc-main')?.scrollTo({ top: 0, behavior: 'smooth' })
                    }}
                  >
                    {prev.title}
                  </EuiButton>
                )}
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                {next && (
                  <EuiButton
                    iconType="arrowRight"
                    iconSide="right"
                    fill
                    onClick={() => {
                      navigate(`/Documentation/${next.slug}`)
                      document.querySelector('.cv-doc-main')?.scrollTo({ top: 0, behavior: 'smooth' })
                    }}
                  >
                    {next.title}
                  </EuiButton>
                )}
              </EuiFlexItem>
            </EuiFlexGroup>
          </>
        ) : (
          <EuiCallOut title="Page introuvable" color="warning" iconType="alert">
            <p>Aucune page ne correspond au slug « {currentSlug} ».</p>
          </EuiCallOut>
        )}
      </main>

      {/* TOC intra-page (H2/H3) */}
      {pageToc.length > 0 && (
        <aside className="cv-doc-toc">
          <EuiText size="xs" color="subdued">
            <p style={{ marginBottom: 6, fontWeight: 600 }}>
              <EuiIcon type="list" size="s" /> Sur cette page
            </p>
          </EuiText>
          <ul>
            {pageToc.map((h) => (
              <li
                key={h.id}
                className={`cv-doc-toc-item cv-doc-toc-level-${h.level}`}
              >
                <a
                  href={`#${h.id}`}
                  onClick={(e) => {
                    e.preventDefault()
                    scrollToAnchor(h.id)
                  }}
                >
                  {h.text}
                </a>
              </li>
            ))}
          </ul>
        </aside>
      )}
    </div>
  )
}
